import React, { useState, useContext } from "react";
import { AlunoContext } from "../context/AlunoContext";
import { useNotification } from "../hooks/useNotification";
import "./AlunoForm.css";

const nacionalidades = [
  "Moçambique",
  "África do Sul",
  "Angola",
  "Portugal",
  "Brasil",
  "Outro",
];

const naturalidades = {
  Moçambique: ["Maputo","Matola","Beira","Nampula","Quelimane","Tete","Chimoio","Inhambane","Xai-Xai","Lichinga","Pemba"],
  "África do Sul": ["Pretória","Joanesburgo","Cidade do Cabo","Durban","Bloemfontein"],
  Angola: ["Luanda","Benguela","Huambo","Lubango","Cabinda"],
  Portugal: ["Lisboa","Porto","Coimbra","Faro","Braga"],
  Brasil: ["São Paulo","Rio de Janeiro","Brasília","Salvador","Belo Horizonte"],
  Outro: [],
};

const initialFormData = {
  nome_completo: "",
  genero: "",
  data_nascimento: "",
  nacionalidade: "",
  naturalidade: "",
  tipo_documento: "",
  numero_documento: "",
  classe: "",
  ano_lectivo: new Date().getFullYear().toString(),
  telefone_responsavel: "",
  email: "",
  nome_pai: "",
  telefone_pai: "",
  nome_mae: "",
  telefone_mae: "",
  historico_medico: "",
  deficiencia: "",
  doenca_cronica: "",
  outras_condicoes: "",
  valor_matricula: "",
  base_mensal: "",
};

const AlunoForm = () => {
  const { fetchAlunos, setAlunoSelecionado, setMostrarModal } = useContext(AlunoContext);
  const { notifySuccess, notifyError } = useNotification();

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "nacionalidade" ? { naturalidade: "" } : {}),
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validarFormulario = () => {
    const errors = {};
    const biRegex = /^[0-9]{12}[A-Z]$/;

    // Campos obrigatórios
    const obrigatorios = ["nome_completo","genero","data_nascimento","nacionalidade","classe","telefone_responsavel","valor_matricula","ano_lectivo","base_mensal"];
    obrigatorios.forEach((campo) => {
      if (!formData[campo]) errors[campo] = "Campo obrigatório";
    });

    // BI válido
    if (formData.tipo_documento === "BI" && formData.numero_documento && !biRegex.test(formData.numero_documento)) {
      errors.numero_documento = "Número de BI inválido (12 dígitos + 1 letra maiúscula)";
    }

    // Email opcional, mas válido
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Formato de email inválido";
    }

    // Valores financeiros obrigatórios e >0
    if (!formData.valor_matricula || parseFloat(formData.valor_matricula) <= 0) {
      errors.valor_matricula = "Insira um valor válido (>0)";
    }
    if (!formData.base_mensal || parseFloat(formData.base_mensal) <= 0) {
      errors.base_mensal = "Insira um valor válido (>0)";
    }

    return errors;
  };

  const handleCancelar = () => {
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validarFormulario();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      notifyError("Por favor, corrija os erros no formulário.");
      return;
    }

    const dataMatricula = new Date().toISOString().split("T")[0];

    try {
      const payload = {
        ...formData,
        valor_matricula: parseFloat(parseFloat(formData.valor_matricula).toFixed(2)),
        base_mensal: parseFloat(parseFloat(formData.base_mensal).toFixed(2)),
        ativo: false,
        data_matricula: dataMatricula,
      };

      const response = await fetch("/api/alunos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      const result = JSON.parse(text);

      if (!response.ok) {
        if (result.message?.toLowerCase().includes("e-mail"))
          setFormErrors((prev) => ({ ...prev, email: "Este e-mail já está em uso." }));
        notifyError(result.message || "Erro no cadastro do aluno.");
        return;
      }

      notifySuccess("Aluno cadastrado com sucesso!");
      fetchAlunos();
      setAlunoSelecionado(result.aluno || result.data?.aluno || result);
      setMostrarModal(true);
      setFormData(initialFormData);
      setFormErrors({});
    } catch (err) {
      notifyError("Erro ao cadastrar aluno. Verifique os dados e tente novamente.");
    }
  };

  return (
    <form className="aluno-form" onSubmit={handleSubmit}>
      <h2>Formulário de Matrícula</h2>

      {/* Identificação */}
      <h3 className="form-section-title">Identificação do Aluno</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="nome_completo">Nome Completo *</label>
          <input id="nome_completo" type="text" name="nome_completo" value={formData.nome_completo} onChange={handleChange} />
          {formErrors.nome_completo && <div className="error">{formErrors.nome_completo}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="genero">Gênero *</label>
          <select id="genero" name="genero" value={formData.genero} onChange={handleChange}>
            <option value="">Selecione</option>
            <option value="Masculino">Masculino</option>
            <option value="Feminino">Feminino</option>
            <option value="Outro">Outro</option>
          </select>
          {formErrors.genero && <div className="error">{formErrors.genero}</div>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="data_nascimento">Data de Nascimento *</label>
          <input id="data_nascimento" type="date" name="data_nascimento" value={formData.data_nascimento} onChange={handleChange} />
          {formErrors.data_nascimento && <div className="error">{formErrors.data_nascimento}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="nacionalidade">Nacionalidade *</label>
          <select id="nacionalidade" name="nacionalidade" value={formData.nacionalidade} onChange={handleChange}>
            <option value="">Selecione</option>
            {nacionalidades.map((pais) => <option key={pais} value={pais}>{pais}</option>)}
          </select>
          {formErrors.nacionalidade && <div className="error">{formErrors.nacionalidade}</div>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="naturalidade">Naturalidade</label>
          <select id="naturalidade" name="naturalidade" value={formData.naturalidade} onChange={handleChange} disabled={!formData.nacionalidade || formData.nacionalidade === "Outro"}>
            <option value="">Selecione</option>
            {formData.nacionalidade && naturalidades[formData.nacionalidade]?.map((cidade) => <option key={cidade} value={cidade}>{cidade}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="tipo_documento">Tipo de Documento</label>
          <select id="tipo_documento" name="tipo_documento" value={formData.tipo_documento} onChange={handleChange}>
            <option value="">Selecione</option>
            <option value="BI">Bilhete de Identidade</option>
            <option value="Cedula">Cédula</option>
            <option value="Passaporte">Passaporte</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="numero_documento">Número do Documento</label>
        <input
          id="numero_documento"
          type="text"
          name="numero_documento"
          value={formData.numero_documento}
          onChange={handleChange}
          placeholder={
            formData.tipo_documento === "BI" ? "12 dígitos + 1 letra (ex: 123456789012A)" :
            formData.tipo_documento === "Cedula" ? "Digite o número da Cédula" :
            formData.tipo_documento === "Passaporte" ? "Digite o número do Passaporte" :
            "Informe o número do documento"
          }
        />
        {formErrors.numero_documento && <div className="error">{formErrors.numero_documento}</div>}
      </div>

      {/* Acadêmico */}
      <h3 className="form-section-title">Dados Acadêmicos</h3>
     <div className="form-row">

  <div className="form-group">
    <label htmlFor="classe">Classe *</label>
    <select
      id="classe"
      name="classe"
      value={formData.classe}
      onChange={handleChange}
    >
      <option value="">Selecione</option>
      {Array.from({ length: 12 }, (_, i) => `${i + 1}ª Classe`).map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>

    {formErrors.classe && (
      <div className="error">{formErrors.classe}</div>
    )}
  </div>

  <div className="form-group">
    <label htmlFor="ano_lectivo">Ano Lectivo *</label>

    <select
      id="ano_lectivo"
      name="ano_lectivo"
      value={formData.ano_lectivo}
      onChange={handleChange}
    >
      {[2025, 2026, 2027, 2028, 2029, 2030].map((ano) => (
        <option key={ano} value={ano}>
          {ano}
        </option>
      ))}
    </select>
  </div>

</div>

      {/* Contatos */}
      <h3 className="form-section-title">Contatos</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="telefone_responsavel">Telefone do Responsável *</label>
          <input id="telefone_responsavel" type="tel" name="telefone_responsavel" value={formData.telefone_responsavel} onChange={handleChange} />
          {formErrors.telefone_responsavel && <div className="error">{formErrors.telefone_responsavel}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} />
          {formErrors.email && <div className="error">{formErrors.email}</div>}
        </div>
      </div>

      {/* Família */}
      <h3 className="form-section-title">Dados Familiares</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="nome_pai">Nome do Pai</label>
          <input id="nome_pai" type="text" name="nome_pai" value={formData.nome_pai} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="telefone_pai">Telefone do Pai</label>
          <input id="telefone_pai" type="tel" name="telefone_pai" value={formData.telefone_pai} onChange={handleChange} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="nome_mae">Nome da Mãe</label>
          <input id="nome_mae" type="text" name="nome_mae" value={formData.nome_mae} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="telefone_mae">Telefone da Mãe</label>
          <input id="telefone_mae" type="tel" name="telefone_mae" value={formData.telefone_mae} onChange={handleChange} />
        </div>
      </div>

      {/* Saúde */}
      <h3 className="form-section-title">Saúde</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="historico_medico">Histórico Médico</label>
          <textarea id="historico_medico" name="historico_medico" value={formData.historico_medico} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="deficiencia">Deficiência</label>
          <input id="deficiencia" type="text" name="deficiencia" value={formData.deficiencia} onChange={handleChange} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="doenca_cronica">Doença Crônica</label>
          <input id="doenca_cronica" type="text" name="doenca_cronica" value={formData.doenca_cronica} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="outras_condicoes">Outras Condições</label>
          <input id="outras_condicoes" type="text" name="outras_condicoes" value={formData.outras_condicoes} onChange={handleChange} />
        </div>
      </div>

      {/* Financeiro */}
      <h3 className="form-section-title">Financeiro</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="valor_matricula">Valor da Matrícula (MZN) *</label>
          <input id="valor_matricula" type="number" name="valor_matricula" value={formData.valor_matricula} onChange={handleChange} min="0" step="0.01" />
          {formErrors.valor_matricula && <div className="error">{formErrors.valor_matricula}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="base_mensal">Base Mensal (MZN) *</label>
          <input id="base_mensal" type="number" name="base_mensal" value={formData.base_mensal} onChange={handleChange} min="0" step="0.01" />
          {formErrors.base_mensal && <div className="error">{formErrors.base_mensal}</div>}
        </div>
      </div>

      <div className="form-buttons">
        <button type="submit" className="btn-primary">Enviar</button>
        <button type="button" onClick={handleCancelar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
};

export default AlunoForm;
